'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';

// Question categories and types
type QuestionCategory = 'system-design' | 'coding' | 'behavioral' | 'technical-depth' | 'leadership';
type DifficultyLevel = 'junior' | 'mid' | 'senior' | 'staff' | 'principal';
type InterviewType = 'phone-screen' | 'technical' | 'system-design' | 'behavioral' | 'final-round';

interface Question {
  id: string;
  category: QuestionCategory;
  difficulty: DifficultyLevel;
  question: string;
  followUps: string[];
  evaluationCriteria: string[];
  timeMinutes: number;
  isPro?: boolean;
}

interface PanelMember {
  id: string;
  name: string;
  role: string;
  interviewType: InterviewType;
  duration: number;
}

interface ScoringCriterion {
  id: string;
  criterion: string;
  weight: number;
  description: string;
}

// Sample questions (50 free, rest are Pro)
const QUESTION_BANK: Question[] = [
  // System Design (10 free)
  {
    id: 'sd-1',
    category: 'system-design',
    difficulty: 'senior',
    question: 'Design a URL shortener like bit.ly',
    followUps: [
      'How would you handle 10M requests per second?',
      'What database would you use and why?',
      'How would you implement analytics tracking?',
      'How would you prevent abuse?',
    ],
    evaluationCriteria: [
      'Scalability considerations',
      'Database design',
      'API design',
      'Caching strategy',
      'Trade-off analysis',
    ],
    timeMinutes: 45,
  },
  {
    id: 'sd-2',
    category: 'system-design',
    difficulty: 'staff',
    question: 'Design a distributed rate limiting system',
    followUps: [
      'How do you handle consistency across multiple servers?',
      'What algorithm would you use (token bucket, sliding window)?',
      'How would you handle clock skew?',
    ],
    evaluationCriteria: [
      'Distributed systems understanding',
      'CAP theorem application',
      'Algorithm selection',
      'Edge case handling',
    ],
    timeMinutes: 60,
  },
  {
    id: 'sd-3',
    category: 'system-design',
    difficulty: 'mid',
    question: 'Design a real-time chat application',
    followUps: [
      'How would you implement read receipts?',
      'What protocol would you use for real-time communication?',
      'How would you handle message persistence?',
    ],
    evaluationCriteria: [
      'WebSocket understanding',
      'Message queue design',
      'Database schema',
      'Scalability planning',
    ],
    timeMinutes: 45,
  },
  {
    id: 'sd-4',
    category: 'system-design',
    difficulty: 'senior',
    question: 'Design a content delivery network (CDN)',
    followUps: [
      'How do you decide where to place edge servers?',
      'How do you handle cache invalidation?',
      'How do you optimize for video streaming vs static assets?',
    ],
    evaluationCriteria: [
      'Geographic distribution strategy',
      'Caching policies',
      'Load balancing',
      'Cost optimization',
    ],
    timeMinutes: 60,
  },
  {
    id: 'sd-5',
    category: 'system-design',
    difficulty: 'principal',
    question: 'Design a globally distributed database with strong consistency',
    followUps: [
      'How do you handle network partitions?',
      'What consensus algorithm would you use?',
      'How do you balance consistency vs latency?',
    ],
    evaluationCriteria: [
      'Consensus algorithms (Paxos, Raft)',
      'CAP theorem mastery',
      'Trade-off analysis',
      'Production experience',
    ],
    timeMinutes: 60,
  },
  {
    id: 'sd-6',
    category: 'system-design',
    difficulty: 'mid',
    question: 'Design a notification service (email, SMS, push)',
    followUps: [
      'How do you ensure delivery reliability?',
      'How do you handle retry logic?',
      'How do you prevent duplicate notifications?',
    ],
    evaluationCriteria: [
      'Queue design',
      'Retry strategies',
      'Idempotency',
      'Scalability',
    ],
    timeMinutes: 45,
  },
  {
    id: 'sd-7',
    category: 'system-design',
    difficulty: 'senior',
    question: 'Design a search engine autocomplete system',
    followUps: [
      'How do you handle typos and suggestions?',
      'How do you rank suggestions?',
      'How do you personalize results?',
    ],
    evaluationCriteria: [
      'Trie data structure understanding',
      'Ranking algorithms',
      'Caching strategies',
      'Low-latency design',
    ],
    timeMinutes: 45,
  },
  {
    id: 'sd-8',
    category: 'system-design',
    difficulty: 'staff',
    question: 'Design a multi-tenant SaaS platform with tenant isolation',
    followUps: [
      'How do you handle database-per-tenant vs shared database?',
      'How do you ensure one tenant cannot access another tenant\'s data?',
      'How do you handle scaling for different tenant sizes?',
    ],
    evaluationCriteria: [
      'Multi-tenancy patterns',
      'Security and isolation',
      'Resource allocation',
      'Cost modeling',
    ],
    timeMinutes: 60,
  },
  {
    id: 'sd-9',
    category: 'system-design',
    difficulty: 'junior',
    question: 'Design a simple URL redirect service',
    followUps: [
      'How would you store the mappings?',
      'How would you generate unique short codes?',
      'What happens if the same URL is shortened twice?',
    ],
    evaluationCriteria: [
      'Basic system design understanding',
      'Database basics',
      'API design',
      'Problem decomposition',
    ],
    timeMinutes: 30,
  },
  {
    id: 'sd-10',
    category: 'system-design',
    difficulty: 'mid',
    question: 'Design a job scheduler system (like cron)',
    followUps: [
      'How do you handle job failures?',
      'How do you prevent duplicate job execution?',
      'How do you scale to millions of jobs?',
    ],
    evaluationCriteria: [
      'Task queue understanding',
      'Distributed locking',
      'Reliability design',
      'Monitoring',
    ],
    timeMinutes: 45,
  },

  // Coding Questions (10 free)
  {
    id: 'code-1',
    category: 'coding',
    difficulty: 'mid',
    question: 'Implement a LRU (Least Recently Used) cache',
    followUps: [
      'What data structures would you use?',
      'What is the time complexity of get and put?',
      'How would you make it thread-safe?',
    ],
    evaluationCriteria: [
      'Data structure knowledge (HashMap + Doubly Linked List)',
      'Time complexity analysis',
      'Code cleanliness',
      'Edge case handling',
    ],
    timeMinutes: 30,
  },
  {
    id: 'code-2',
    category: 'coding',
    difficulty: 'junior',
    question: 'Reverse a string in place',
    followUps: [
      'What if the string contains Unicode characters?',
      'How would you handle null or empty strings?',
    ],
    evaluationCriteria: [
      'Basic algorithm knowledge',
      'Code correctness',
      'Edge case handling',
    ],
    timeMinutes: 15,
  },
  {
    id: 'code-3',
    category: 'coding',
    difficulty: 'senior',
    question: 'Find the median of two sorted arrays',
    followUps: [
      'Can you optimize to O(log(min(m,n))) time?',
      'What if arrays have different sizes?',
    ],
    evaluationCriteria: [
      'Algorithm optimization',
      'Binary search mastery',
      'Edge case handling',
      'Code efficiency',
    ],
    timeMinutes: 45,
  },
  {
    id: 'code-4',
    category: 'coding',
    difficulty: 'mid',
    question: 'Implement a thread-safe singleton pattern',
    followUps: [
      'What are the different ways to implement this?',
      'How do you handle lazy initialization?',
      'What are the trade-offs of each approach?',
    ],
    evaluationCriteria: [
      'Concurrency understanding',
      'Design pattern knowledge',
      'Thread-safety awareness',
    ],
    timeMinutes: 20,
  },
  {
    id: 'code-5',
    category: 'coding',
    difficulty: 'senior',
    question: 'Design and implement a rate limiter class',
    followUps: [
      'Which algorithm would you use (token bucket, sliding window)?',
      'How do you make it thread-safe?',
      'How would you test this?',
    ],
    evaluationCriteria: [
      'Algorithm selection',
      'Concurrency handling',
      'API design',
      'Testing approach',
    ],
    timeMinutes: 30,
  },
  {
    id: 'code-6',
    category: 'coding',
    difficulty: 'mid',
    question: 'Detect a cycle in a linked list',
    followUps: [
      'Can you do it with O(1) space?',
      'How would you find the start of the cycle?',
    ],
    evaluationCriteria: [
      'Algorithm knowledge (Floyd\'s cycle detection)',
      'Space complexity optimization',
      'Problem-solving approach',
    ],
    timeMinutes: 25,
  },
  {
    id: 'code-7',
    category: 'coding',
    difficulty: 'junior',
    question: 'Check if a string is a palindrome',
    followUps: [
      'How do you handle spaces and punctuation?',
      'What about case sensitivity?',
    ],
    evaluationCriteria: [
      'Basic problem solving',
      'String manipulation',
      'Edge cases',
    ],
    timeMinutes: 15,
  },
  {
    id: 'code-8',
    category: 'coding',
    difficulty: 'staff',
    question: 'Implement a lock-free concurrent queue',
    followUps: [
      'What are the challenges with lock-free data structures?',
      'How do you handle the ABA problem?',
      'When would you use this vs a locked queue?',
    ],
    evaluationCriteria: [
      'Advanced concurrency knowledge',
      'Lock-free programming',
      'Trade-off analysis',
      'Performance understanding',
    ],
    timeMinutes: 60,
  },
  {
    id: 'code-9',
    category: 'coding',
    difficulty: 'mid',
    question: 'Implement a binary search tree with insert, delete, and search',
    followUps: [
      'How do you handle deletion with two children?',
      'What is the time complexity in worst case?',
      'How would you balance the tree?',
    ],
    evaluationCriteria: [
      'Tree data structure knowledge',
      'Algorithm implementation',
      'Complexity analysis',
    ],
    timeMinutes: 35,
  },
  {
    id: 'code-10',
    category: 'coding',
    difficulty: 'senior',
    question: 'Implement a distributed lock using Redis',
    followUps: [
      'How do you handle lock expiration?',
      'What happens if the client dies while holding the lock?',
      'How do you prevent deadlocks?',
    ],
    evaluationCriteria: [
      'Distributed systems knowledge',
      'Redis understanding',
      'Edge case handling',
      'Production awareness',
    ],
    timeMinutes: 40,
  },

  // Behavioral Questions (10 free)
  {
    id: 'beh-1',
    category: 'behavioral',
    difficulty: 'mid',
    question: 'Tell me about a time you had to make a difficult technical decision with incomplete information',
    followUps: [
      'How did you gather the information you needed?',
      'What was the outcome?',
      'What would you do differently?',
    ],
    evaluationCriteria: [
      'Decision-making process',
      'Risk assessment',
      'Outcome orientation',
      'Learning from experience',
    ],
    timeMinutes: 15,
  },
  {
    id: 'beh-2',
    category: 'behavioral',
    difficulty: 'senior',
    question: 'Describe a situation where you had to convince your team to adopt a technology they were resistant to',
    followUps: [
      'What was their main objection?',
      'How did you address their concerns?',
      'Was the adoption successful?',
    ],
    evaluationCriteria: [
      'Influencing skills',
      'Stakeholder management',
      'Technical leadership',
      'Change management',
    ],
    timeMinutes: 15,
  },
  {
    id: 'beh-3',
    category: 'behavioral',
    difficulty: 'staff',
    question: 'Tell me about a time you made a technical decision that you later regretted',
    followUps: [
      'What would you have done differently?',
      'How did you handle the consequences?',
      'What did you learn?',
    ],
    evaluationCriteria: [
      'Self-awareness',
      'Learning from mistakes',
      'Ownership',
      'Growth mindset',
    ],
    timeMinutes: 15,
  },
  {
    id: 'beh-4',
    category: 'behavioral',
    difficulty: 'mid',
    question: 'Describe a conflict you had with a team member and how you resolved it',
    followUps: [
      'What was the root cause?',
      'How did you approach the conversation?',
      'What was the outcome?',
    ],
    evaluationCriteria: [
      'Conflict resolution',
      'Emotional intelligence',
      'Communication skills',
      'Team collaboration',
    ],
    timeMinutes: 15,
  },
  {
    id: 'beh-5',
    category: 'behavioral',
    difficulty: 'principal',
    question: 'Tell me about a time you had to balance technical debt with feature delivery',
    followUps: [
      'How did you make the trade-off decision?',
      'How did you communicate this to stakeholders?',
      'What was the long-term impact?',
    ],
    evaluationCriteria: [
      'Strategic thinking',
      'Stakeholder communication',
      'Long-term planning',
      'Business acumen',
    ],
    timeMinutes: 20,
  },
  {
    id: 'beh-6',
    category: 'behavioral',
    difficulty: 'senior',
    question: 'Describe your most challenging debugging experience',
    followUps: [
      'How did you approach the problem?',
      'What tools did you use?',
      'How long did it take to resolve?',
    ],
    evaluationCriteria: [
      'Problem-solving methodology',
      'Persistence',
      'Tool proficiency',
      'Learning approach',
    ],
    timeMinutes: 15,
  },
  {
    id: 'beh-7',
    category: 'behavioral',
    difficulty: 'junior',
    question: 'Tell me about a project you are most proud of',
    followUps: [
      'What was your specific contribution?',
      'What challenges did you face?',
      'What did you learn?',
    ],
    evaluationCriteria: [
      'Technical contribution',
      'Problem-solving',
      'Team collaboration',
      'Passion for work',
    ],
    timeMinutes: 10,
  },
  {
    id: 'beh-8',
    category: 'behavioral',
    difficulty: 'staff',
    question: 'Describe a time you mentored a junior engineer. What approach did you take?',
    followUps: [
      'What were the biggest challenges?',
      'How did you measure success?',
      'What did you learn from the experience?',
    ],
    evaluationCriteria: [
      'Mentorship skills',
      'Patience and empathy',
      'Teaching ability',
      'Investment in others',
    ],
    timeMinutes: 15,
  },
  {
    id: 'beh-9',
    category: 'behavioral',
    difficulty: 'mid',
    question: 'Tell me about a time you received critical feedback. How did you respond?',
    followUps: [
      'Who gave you the feedback?',
      'How did you feel about it?',
      'What actions did you take?',
    ],
    evaluationCriteria: [
      'Receptiveness to feedback',
      'Growth mindset',
      'Self-improvement',
      'Emotional maturity',
    ],
    timeMinutes: 10,
  },
  {
    id: 'beh-10',
    category: 'behavioral',
    difficulty: 'principal',
    question: 'Describe how you would handle a situation where your technical recommendation was overruled by management',
    followUps: [
      'How do you stay motivated when this happens?',
      'How do you prevent this from happening?',
      'What would you do if the decision led to problems?',
    ],
    evaluationCriteria: [
      'Organizational navigation',
      'Stakeholder management',
      'Professionalism',
      'Strategic influence',
    ],
    timeMinutes: 15,
  },

  // Technical Depth Questions (10 free)
  {
    id: 'tech-1',
    category: 'technical-depth',
    difficulty: 'senior',
    question: 'Explain how a database index works and when you would use different types (B-tree, Hash, etc.)',
    followUps: [
      'What are the trade-offs between index types?',
      'How do you decide which columns to index?',
      'What is the cost of over-indexing?',
    ],
    evaluationCriteria: [
      'Database internals knowledge',
      'Index type understanding',
      'Performance optimization',
      'Trade-off analysis',
    ],
    timeMinutes: 20,
  },
  {
    id: 'tech-2',
    category: 'technical-depth',
    difficulty: 'staff',
    question: 'Explain the CAP theorem and how it applies to distributed databases',
    followUps: [
      'Can you give examples of CP, AP, and CA systems?',
      'How does eventual consistency fit in?',
      'What is the PACELC theorem?',
    ],
    evaluationCriteria: [
      'Distributed systems mastery',
      'Theoretical understanding',
      'Practical application',
      'Real-world examples',
    ],
    timeMinutes: 25,
  },
  {
    id: 'tech-3',
    category: 'technical-depth',
    difficulty: 'mid',
    question: 'Explain the difference between authentication and authorization',
    followUps: [
      'What are common authentication methods?',
      'How does OAuth 2.0 work?',
      'What is RBAC vs ABAC?',
    ],
    evaluationCriteria: [
      'Security fundamentals',
      'Protocol knowledge',
      'Access control patterns',
    ],
    timeMinutes: 15,
  },
  {
    id: 'tech-4',
    category: 'technical-depth',
    difficulty: 'senior',
    question: 'How does garbage collection work in your preferred language?',
    followUps: [
      'What are the different GC algorithms?',
      'How do you tune GC for performance?',
      'When would you use manual memory management?',
    ],
    evaluationCriteria: [
      'Language internals knowledge',
      'Performance tuning',
      'Memory management',
    ],
    timeMinutes: 20,
  },
  {
    id: 'tech-5',
    category: 'technical-depth',
    difficulty: 'principal',
    question: 'Explain consensus algorithms (Paxos, Raft) and when you would use them',
    followUps: [
      'What are the key differences between Paxos and Raft?',
      'How do they handle leader election?',
      'What are the performance implications?',
    ],
    evaluationCriteria: [
      'Distributed consensus mastery',
      'Algorithm comparison',
      'Production experience',
      'Trade-off understanding',
    ],
    timeMinutes: 30,
  },
  {
    id: 'tech-6',
    category: 'technical-depth',
    difficulty: 'mid',
    question: 'Explain how HTTP/2 differs from HTTP/1.1',
    followUps: [
      'What is multiplexing?',
      'How does server push work?',
      'When would you use HTTP/3?',
    ],
    evaluationCriteria: [
      'Protocol knowledge',
      'Performance optimization',
      'Modern web standards',
    ],
    timeMinutes: 15,
  },
  {
    id: 'tech-7',
    category: 'technical-depth',
    difficulty: 'senior',
    question: 'Explain different types of database transactions and isolation levels',
    followUps: [
      'What is the difference between Read Committed and Serializable?',
      'How do you handle deadlocks?',
      'What are the performance trade-offs?',
    ],
    evaluationCriteria: [
      'ACID understanding',
      'Isolation level mastery',
      'Concurrency control',
      'Production experience',
    ],
    timeMinutes: 25,
  },
  {
    id: 'tech-8',
    category: 'technical-depth',
    difficulty: 'junior',
    question: 'Explain the difference between a process and a thread',
    followUps: [
      'When would you use threads vs processes?',
      'What is context switching?',
      'What are green threads?',
    ],
    evaluationCriteria: [
      'OS fundamentals',
      'Concurrency basics',
      'Use case understanding',
    ],
    timeMinutes: 15,
  },
  {
    id: 'tech-9',
    category: 'technical-depth',
    difficulty: 'staff',
    question: 'Explain how TCP congestion control works',
    followUps: [
      'What are slow start and congestion avoidance?',
      'How does TCP detect packet loss?',
      'What is TCP BBR?',
    ],
    evaluationCriteria: [
      'Network protocol depth',
      'Algorithm understanding',
      'Performance optimization',
    ],
    timeMinutes: 25,
  },
  {
    id: 'tech-10',
    category: 'technical-depth',
    difficulty: 'mid',
    question: 'Explain the difference between SQL and NoSQL databases',
    followUps: [
      'When would you choose one over the other?',
      'What are the different types of NoSQL databases?',
      'Can you give real-world examples of each?',
    ],
    evaluationCriteria: [
      'Database fundamentals',
      'Use case analysis',
      'Trade-off understanding',
    ],
    timeMinutes: 15,
  },

  // Leadership Questions (10 free)
  {
    id: 'lead-1',
    category: 'leadership',
    difficulty: 'staff',
    question: 'How do you handle underperforming team members?',
    followUps: [
      'What is your process for identifying underperformance?',
      'How do you create performance improvement plans?',
      'When do you decide to let someone go?',
    ],
    evaluationCriteria: [
      'Performance management',
      'Coaching skills',
      'Difficult conversations',
      'Team standards',
    ],
    timeMinutes: 20,
  },
  {
    id: 'lead-2',
    category: 'leadership',
    difficulty: 'principal',
    question: 'Describe your approach to building engineering culture',
    followUps: [
      'What are the key elements of a strong culture?',
      'How do you measure culture?',
      'How do you maintain culture as you scale?',
    ],
    evaluationCriteria: [
      'Culture definition',
      'Strategic thinking',
      'Values alignment',
      'Scalability mindset',
    ],
    timeMinutes: 25,
  },
  {
    id: 'lead-3',
    category: 'leadership',
    difficulty: 'staff',
    question: 'How do you prioritize technical debt vs new features?',
    followUps: [
      'How do you communicate this to product managers?',
      'What metrics do you use?',
      'How do you prevent tech debt from accumulating?',
    ],
    evaluationCriteria: [
      'Strategic balance',
      'Stakeholder management',
      'Long-term thinking',
      'Data-driven decisions',
    ],
    timeMinutes: 20,
  },
  {
    id: 'lead-4',
    category: 'leadership',
    difficulty: 'senior',
    question: 'Tell me about your approach to 1-on-1s with your direct reports',
    followUps: [
      'How often do you meet?',
      'What topics do you cover?',
      'How do you track action items?',
    ],
    evaluationCriteria: [
      'People management',
      'Structured approach',
      'Career development focus',
      'Follow-through',
    ],
    timeMinutes: 15,
  },
  {
    id: 'lead-5',
    category: 'leadership',
    difficulty: 'principal',
    question: 'How do you scale yourself as an organization grows from 10 to 100 engineers?',
    followUps: [
      'What changes in your role?',
      'How do you delegate effectively?',
      'What systems and processes do you put in place?',
    ],
    evaluationCriteria: [
      'Scalability thinking',
      'Delegation skills',
      'Process design',
      'Leadership maturity',
    ],
    timeMinutes: 25,
  },
  {
    id: 'lead-6',
    category: 'leadership',
    difficulty: 'staff',
    question: 'Describe your process for making technical decisions with multiple stakeholders',
    followUps: [
      'How do you gather input?',
      'How do you handle disagreement?',
      'How do you communicate the decision?',
    ],
    evaluationCriteria: [
      'Decision-making framework',
      'Stakeholder management',
      'Consensus building',
      'Communication',
    ],
    timeMinutes: 20,
  },
  {
    id: 'lead-7',
    category: 'leadership',
    difficulty: 'senior',
    question: 'How do you keep your team motivated during long, difficult projects?',
    followUps: [
      'What are the signs of low morale?',
      'What interventions have you tried?',
      'How do you celebrate progress?',
    ],
    evaluationCriteria: [
      'Team motivation',
      'Emotional intelligence',
      'Morale monitoring',
      'Recognition practices',
    ],
    timeMinutes: 15,
  },
  {
    id: 'lead-8',
    category: 'leadership',
    difficulty: 'principal',
    question: 'How do you create alignment between engineering and product teams?',
    followUps: [
      'What are common sources of misalignment?',
      'How do you resolve conflicts?',
      'What processes help maintain alignment?',
    ],
    evaluationCriteria: [
      'Cross-functional leadership',
      'Stakeholder management',
      'Process design',
      'Conflict resolution',
    ],
    timeMinutes: 25,
  },
  {
    id: 'lead-9',
    category: 'leadership',
    difficulty: 'staff',
    question: 'Describe your approach to promoting engineers on your team',
    followUps: [
      'What criteria do you use?',
      'How do you ensure fairness and consistency?',
      'How do you handle disagreements about promotions?',
    ],
    evaluationCriteria: [
      'Career development',
      'Fairness and equity',
      'Criteria clarity',
      'Communication',
    ],
    timeMinutes: 20,
  },
  {
    id: 'lead-10',
    category: 'leadership',
    difficulty: 'senior',
    question: 'How do you handle disagreements with your manager or CEO about technical direction?',
    followUps: [
      'Can you give a specific example?',
      'How do you present your case?',
      'What do you do if you still disagree after the decision is made?',
    ],
    evaluationCriteria: [
      'Upward management',
      'Influence without authority',
      'Professionalism',
      'Strategic thinking',
    ],
    timeMinutes: 20,
  },
];

const CATEGORY_LABELS: Record<QuestionCategory, string> = {
  'system-design': 'System Design',
  'coding': 'Coding',
  'behavioral': 'Behavioral',
  'technical-depth': 'Technical Depth',
  'leadership': 'Leadership',
};

const DIFFICULTY_LABELS: Record<DifficultyLevel, string> = {
  'junior': 'Junior (0-2 yrs)',
  'mid': 'Mid-Level (2-5 yrs)',
  'senior': 'Senior (5-8 yrs)',
  'staff': 'Staff (8-12 yrs)',
  'principal': 'Principal (12+ yrs)',
};

const INTERVIEW_TYPE_LABELS: Record<InterviewType, string> = {
  'phone-screen': 'Phone Screen (30 min)',
  'technical': 'Technical Interview (45 min)',
  'system-design': 'System Design (60 min)',
  'behavioral': 'Behavioral Interview (30 min)',
  'final-round': 'Final Round (60 min)',
};

export default function InterviewQuestionBankPage() {
  // Question Bank State
  const [selectedCategory, setSelectedCategory] = useState<QuestionCategory | 'all'>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<DifficultyLevel | 'all'>('all');
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set());

  // Interview Panel State
  const [panelMembers, setPanelMembers] = useState<PanelMember[]>([
    { id: '1', name: '', role: '', interviewType: 'technical', duration: 45 },
  ]);

  // Scoring Rubric State
  const [scoringCriteria, setScoringCriteria] = useState<ScoringCriterion[]>([
    { id: '1', criterion: 'Technical Skills', weight: 30, description: 'Coding ability, system design, problem solving' },
    { id: '2', criterion: 'Communication', weight: 25, description: 'Clarity, listening, asking questions' },
    { id: '3', criterion: 'Culture Fit', weight: 20, description: 'Values alignment, team collaboration' },
    { id: '4', criterion: 'Experience Relevance', weight: 15, description: 'Background matches role needs' },
    { id: '5', criterion: 'Growth Mindset', weight: 10, description: 'Learning ability, adaptability' },
  ]);

  // Filter questions
  const filteredQuestions = useMemo(() => {
    return QUESTION_BANK.filter((q) => {
      if (selectedCategory !== 'all' && q.category !== selectedCategory) return false;
      if (selectedDifficulty !== 'all' && q.difficulty !== selectedDifficulty) return false;
      return true;
    });
  }, [selectedCategory, selectedDifficulty]);

  // Toggle question selection
  const toggleQuestion = (id: string) => {
    const newSelected = new Set(selectedQuestions);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedQuestions(newSelected);
  };

  // Panel member functions
  const addPanelMember = () => {
    if (panelMembers.length >= 5) {
      alert('Free tier limited to 5 panel members. Upgrade to Pro for unlimited members.');
      return;
    }
    setPanelMembers([
      ...panelMembers,
      { id: Date.now().toString(), name: '', role: '', interviewType: 'technical', duration: 45 },
    ]);
  };

  const updatePanelMember = (id: string, field: keyof PanelMember, value: string | number) => {
    setPanelMembers(panelMembers.map((m) => (m.id === id ? { ...m, [field]: value } : m)));
  };

  const removePanelMember = (id: string) => {
    setPanelMembers(panelMembers.filter((m) => m.id !== id));
  };

  // Scoring criterion functions
  const addCriterion = () => {
    setScoringCriteria([
      ...scoringCriteria,
      { id: Date.now().toString(), criterion: '', weight: 0, description: '' },
    ]);
  };

  const updateCriterion = (id: string, field: keyof ScoringCriterion, value: string | number) => {
    setScoringCriteria(scoringCriteria.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
  };

  const removeCriterion = (id: string) => {
    setScoringCriteria(scoringCriteria.filter((c) => c.id !== id));
  };

  // Calculate total weight
  const totalWeight = useMemo(() => {
    return scoringCriteria.reduce((sum, c) => sum + c.weight, 0);
  }, [scoringCriteria]);

  // Calculate total interview time
  const totalInterviewTime = useMemo(() => {
    return panelMembers.reduce((sum, m) => sum + (m.duration || 0), 0);
  }, [panelMembers]);

  // Export to Excel
  const exportToExcel = () => {
    if (typeof window === 'undefined') return;

    // Load xlsx from CDN
    const script = document.createElement('script');
    script.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
    script.onload = () => {
      const XLSX = (window as any).XLSX;

      // Create workbook
      const wb = XLSX.utils.book_new();

      // Sheet 1: Selected Questions
      const questionsData = Array.from(selectedQuestions)
        .map((id) => QUESTION_BANK.find((q) => q.id === id))
        .filter(Boolean)
        .map((q) => ({
          Category: CATEGORY_LABELS[q!.category],
          Difficulty: DIFFICULTY_LABELS[q!.difficulty],
          Question: q!.question,
          'Follow-ups': q!.followUps.join('\n'),
          'Evaluation Criteria': q!.evaluationCriteria.join('\n'),
          'Time (min)': q!.timeMinutes,
        }));

      if (questionsData.length > 0) {
        const ws1 = XLSX.utils.json_to_sheet(questionsData);
        ws1['!cols'] = [
          { wch: 15 },
          { wch: 15 },
          { wch: 50 },
          { wch: 40 },
          { wch: 40 },
          { wch: 10 },
        ];
        XLSX.utils.book_append_sheet(wb, ws1, 'Questions');
      }

      // Sheet 2: Interview Panel
      const panelData = panelMembers
        .filter((m) => m.name.trim())
        .map((m) => ({
          Name: m.name,
          Role: m.role,
          'Interview Type': INTERVIEW_TYPE_LABELS[m.interviewType],
          'Duration (min)': m.duration,
        }));

      if (panelData.length > 0) {
        const ws2 = XLSX.utils.json_to_sheet(panelData);
        ws2['!cols'] = [{ wch: 20 }, { wch: 25 }, { wch: 25 }, { wch: 15 }];
        XLSX.utils.book_append_sheet(wb, ws2, 'Interview Panel');

        // Add total row
        XLSX.utils.sheet_add_aoa(
          ws2,
          [['', '', 'Total Time:', totalInterviewTime]],
          { origin: -1 }
        );
      }

      // Sheet 3: Scoring Rubric
      const rubricData = scoringCriteria.map((c) => ({
        Criterion: c.criterion,
        'Weight (%)': c.weight,
        Description: c.description,
        'Score (1-5)': '',
        'Weighted Score': `=D${scoringCriteria.indexOf(c) + 2}*B${scoringCriteria.indexOf(c) + 2}/100`,
      }));

      const ws3 = XLSX.utils.json_to_sheet(rubricData);
      ws3['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 40 }, { wch: 12 }, { wch: 15 }];

      // Add total row
      const lastRow = rubricData.length + 2;
      XLSX.utils.sheet_add_aoa(
        ws3,
        [
          ['Total', totalWeight, '', '', `=SUM(E2:E${lastRow - 1})`],
          ['', '', '', '', ''],
          ['Final Score (out of 5)', '', '', '', `=E${lastRow}/20`],
        ],
        { origin: -1 }
      );

      XLSX.utils.book_append_sheet(wb, ws3, 'Scoring Rubric');

      // Enable auto-calculation
      (wb.Workbook as any) = { CalcPr: { fullCalcOnLoad: true } };

      // Download
      XLSX.writeFile(wb, 'interview-scorecard.xlsx');
    };
    document.head.appendChild(script);
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            href="/tools"
            className="text-accent hover:text-accent mb-4 inline-block"
          >
            ← Back to Tools
          </Link>
          <h1 className="text-4xl font-bold mb-4 text-light-text dark:text-foreground">
            Technical Interview Question Bank & Scorer
          </h1>
          <p className="text-xl text-light-text-secondary dark:text-foreground-secondary">
            500+ curated interview questions with scoring rubrics and panel planning tools.
            Build comprehensive interview loops and consistent evaluation criteria.
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-300 dark:border-border mb-8">
          <div className="flex gap-8">
            <button className="pb-3 border-b-2 border-accent text-accent font-semibold">
              Question Bank
            </button>
            <button className="pb-3 border-b-2 border-transparent text-light-text-secondary dark:text-foreground-tertiary hover:text-accent hover:dark:text-accent">
              Interview Panel
            </button>
            <button className="pb-3 border-b-2 border-transparent text-light-text-secondary dark:text-foreground-tertiary hover:text-accent hover:dark:text-accent">
              Scoring Rubric
            </button>
          </div>
        </div>

        {/* Question Bank Section */}
        <div className="space-y-8">
          {/* Filters */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as QuestionCategory | 'all')}
                className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-surface text-light-text dark:text-foreground"
              >
                <option value="all">All Categories</option>
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                Difficulty Level
              </label>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value as DifficultyLevel | 'all')}
                className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-surface text-light-text dark:text-foreground"
              >
                <option value="all">All Levels</option>
                {Object.entries(DIFFICULTY_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-accent-muted border border-border rounded-lg p-4">
              <div className="text-2xl font-bold text-accent">{filteredQuestions.length}</div>
              <div className="text-sm text-light-text-secondary dark:text-foreground-secondary">Questions Available</div>
            </div>
            <div className="bg-accent-muted border border-border rounded-lg p-4">
              <div className="text-2xl font-bold text-accent">{selectedQuestions.size}</div>
              <div className="text-sm text-light-text-secondary dark:text-foreground-secondary">Questions Selected</div>
            </div>
            <div className="bg-accent-muted border border-border rounded-lg p-4">
              <div className="text-2xl font-bold text-accent">
                {Array.from(selectedQuestions)
                  .map((id) => QUESTION_BANK.find((q) => q.id === id)?.timeMinutes || 0)
                  .reduce((sum, t) => sum + t, 0)}
                m
              </div>
              <div className="text-sm text-light-text-secondary dark:text-foreground-secondary">Total Interview Time</div>
            </div>
          </div>

          {/* Question List */}
          <div className="space-y-4">
            {filteredQuestions.map((question) => (
              <div
                key={question.id}
                className={`border rounded-lg p-6 transition-colors cursor-pointer ${
                  selectedQuestions.has(question.id)
                    ? 'border-accent bg-accent-muted'
                    : 'border-gray-300 dark:border-border hover:border-border-muted0'
                }`}
                onClick={() => toggleQuestion(question.id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <input
                        type="checkbox"
                        checked={selectedQuestions.has(question.id)}
                        onChange={() => toggleQuestion(question.id)}
                        className="w-5 h-5"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="text-xs px-2 py-1 bg-accent-muted text-accent rounded">
                        {CATEGORY_LABELS[question.category]}
                      </span>
                      <span className="text-xs px-2 py-1 bg-gray-200 dark:bg-surface text-light-text dark:text-foreground rounded">
                        {DIFFICULTY_LABELS[question.difficulty]}
                      </span>
                      <span className="text-xs text-light-text-secondary dark:text-foreground-tertiary">
                        {question.timeMinutes} min
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-light-text dark:text-foreground mb-3">
                      {question.question}
                    </h3>
                  </div>
                </div>

                {/* Follow-ups */}
                <div className="mb-3">
                  <div className="text-sm font-medium text-light-text dark:text-foreground mb-2">
                    Follow-up Questions:
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                    {question.followUps.map((fu, idx) => (
                      <li key={idx}>{fu}</li>
                    ))}
                  </ul>
                </div>

                {/* Evaluation Criteria */}
                <div>
                  <div className="text-sm font-medium text-light-text dark:text-foreground mb-2">
                    What to Evaluate:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {question.evaluationCriteria.map((criterion, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-2 py-1 bg-gray-200 dark:bg-surface text-light-text dark:text-foreground rounded"
                      >
                        {criterion}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Export Button */}
          <div className="flex justify-end">
            <button
              onClick={exportToExcel}
              disabled={selectedQuestions.size === 0}
              className="px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              📊 Export to Excel ({selectedQuestions.size} questions)
            </button>
          </div>

          {/* Pro Features Callout */}
          <div className="bg-gradient-to-r from-accent-muted to-accent/5 border-l-4 border-accent p-6 rounded-lg">
            <h3 className="text-xl font-bold text-accent mb-3">
              ⭐ Unlock Pro Features
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <div className="font-semibold text-light-text dark:text-foreground mb-2">
                  Pro ($29/month):
                </div>
                <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                  <li>✓ 500+ interview questions (vs 50 free)</li>
                  <li>✓ Custom question builder</li>
                  <li>✓ Advanced scoring rubrics</li>
                  <li>✓ Interview panel optimizer</li>
                  <li>✓ Excel export with formulas</li>
                </ul>
              </div>
              <div>
                <div className="font-semibold text-light-text dark:text-foreground mb-2">
                  Team ($99/month):
                </div>
                <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                  <li>✓ Everything in Pro</li>
                  <li>✓ Unlimited custom questions</li>
                  <li>✓ AI-powered answer evaluation</li>
                  <li>✓ Video interview integration</li>
                  <li>✓ Historical hiring analytics</li>
                </ul>
              </div>
            </div>
            <Link
              href="/pricing"
              className="inline-block px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
            >
              Upgrade to Pro →
            </Link>
          </div>

          {/* How It Works */}
          <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
            <h3 className="text-xl font-bold mb-4 text-light-text dark:text-foreground">
              How to Use This Tool
            </h3>
            <div className="space-y-4 text-light-text-secondary dark:text-foreground-secondary">
              <div>
                <div className="font-semibold text-light-text dark:text-foreground mb-2">
                  1. Select Questions
                </div>
                <p className="text-sm">
                  Filter by category and difficulty level, then select questions for your interview
                  loop. Mix system design, coding, behavioral, and leadership questions based on
                  the role.
                </p>
              </div>
              <div>
                <div className="font-semibold text-light-text dark:text-foreground mb-2">
                  2. Build Interview Panel (Pro)
                </div>
                <p className="text-sm">
                  Plan your interview loop with multiple panel members. Assign interview types and
                  durations to each interviewer for balanced coverage.
                </p>
              </div>
              <div>
                <div className="font-semibold text-light-text dark:text-foreground mb-2">
                  3. Create Scoring Rubric
                </div>
                <p className="text-sm">
                  Define evaluation criteria with weights. Use the Excel export to score candidates
                  consistently across all interviewers.
                </p>
              </div>
              <div>
                <div className="font-semibold text-light-text dark:text-foreground mb-2">
                  4. Export Scorecard
                </div>
                <p className="text-sm">
                  Download an Excel scorecard with all questions, evaluation criteria, and automated
                  score calculations. Share with your interview panel.
                </p>
              </div>
            </div>
          </div>

          {/* Question Categories Breakdown */}
          <div className="grid md:grid-cols-5 gap-4">
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-accent mb-1">10</div>
              <div className="text-sm text-light-text-secondary dark:text-foreground-secondary">System Design</div>
            </div>
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-accent mb-1">10</div>
              <div className="text-sm text-light-text-secondary dark:text-foreground-secondary">Coding</div>
            </div>
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-accent mb-1">10</div>
              <div className="text-sm text-light-text-secondary dark:text-foreground-secondary">Behavioral</div>
            </div>
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-accent mb-1">10</div>
              <div className="text-sm text-light-text-secondary dark:text-foreground-secondary">Technical Depth</div>
            </div>
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-accent mb-1">10</div>
              <div className="text-sm text-light-text-secondary dark:text-foreground-secondary">Leadership</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
